<template>
  <div class="tabs-wrapper">
    <ul class="tabs-header rbz-text-regular" :class="`marker-${markerStyle}`">
      <template v-for="tab in tabs" :key="tab.title">
        <li
          v-if="!tab.hidden"
          :class="[
            'tab',
            `marker-${markerStyle}`,
            { 'tab-header-grow': tabHeaderGrows },
            { active: selectedTab === tab.title },
            { disabled: selectedTab !== tab.title && tab.disabled },
          ]"
          data-qa="tab"
          @click="
            selectedTab = tab.title;
            clearFocus($event);
            $emit('selected-tab', tab);
          "
          @keyup.space="
            selectedTab = tab.title;
            $emit('selected-tab', tab);
          "
          @keydown.enter="
            selectedTab = tab.title;
            $emit('selected-tab', tab);
          "
          tabindex="0"
        >
          <component v-if="tab.icon" :is="tabIcon(tab)" v-bind="{ color: tabIconColor(tab) }" />
          {{ tab.title }}
        </li>
      </template>
    </ul>
    <div class="content">
      <slot :name="selectedTab"></slot>
    </div>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { getIconComponent } from '../../assets/icons';
import { Tab } from '../../rbz/types';
import _ from 'lodash';

interface TabsProps {
  tabs: Tab[];
  active?: string;
  markerStyle?: 'bottom' | 'top';
  tabHeaderGrows?: boolean;
}

const props = withDefaults(defineProps<TabsProps>(), {
  active: '',
  markerStyle: 'bottom',
  tabHeaderGrows: true,
});

interface TabsWrapperEmits {
  (e: 'selected-tab', tab: Tab): void;
}

const emit = defineEmits<TabsWrapperEmits>();

const selectedTab = ref(props.active || props.tabs[0].title);

const clearFocus = (event: Event) => {
  if (event.pointerType === 'mouse') {
    (event.currentTarget as HTMLElement)?.blur();
  }
};
const tabIcon = (tab: Tab) => {
  return tab.icon ? getIconComponent(tab.icon) : null;
};

const tabIconColor = (tab: Tab): string => {
  if (selectedTab.value === tab.title) {
    return 'mariner';
  }
  if (tab.disabled) {
    return 'silver';
  }
  return 'shuttle-gray';
};

watch(
  () => props.active,
  (value) => {
    selectedTab.value = value;
  }
);

onMounted(() => {
  const tab: Tab = _.find(props.tabs, (tab: Tab) => {
    return tab.title === selectedTab.value;
  });
  emit('selected-tab', tab);
});
</script>
<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

.tabs-header {
  display: flex;
  list-style: none;
  margin-bottom: 24px;
  padding-left: 0;
  text-align: center;

  &.marker-bottom {
    border-bottom: 1px solid map.get(colors.$colors, 'silver');
  }
}

.tab {
  align-items: center;
  color: map.get(colors.$colors, 'shuttle-gray');
  cursor: pointer;
  display: flex;
  gap: 8px;
  justify-content: center;
  min-width: 76px;

  &.marker-bottom {
    padding: 10px 8px;

    &:hover {
      background-color: map.get(colors.$colors, 'white-ice');
    }

    &.disabled {
      color: map.get(colors.$colors, 'silver');
      pointer-events: none;
    }
  }

  &.marker-top {
    background-color: map.get(colors.$colors, 'alabaster');
    border-bottom: 1px solid map.get(colors.$colors, 'silver');
    padding: 13px 16px 9px;

    &:hover {
      background-color: map.get(colors.$colors, 'gallery');
    }

    &.disabled {
      background-color: map.get(colors.$colors, 'alabaster');
      color: map.get(colors.$colors, 'silver');
      pointer-events: none;
    }
  }

  &.tab-header-grow {
    flex-grow: 1;
  }
}

.active {
  color: map.get(colors.$colors, 'jewel');
  cursor: pointer;
  min-width: 76px;

  &.marker-bottom {
    border-bottom: 3px solid map.get(colors.$colors, 'jewel');
    margin-bottom: -1px;
    padding: 10px 8px 9px;

    &:hover {
      background-color: map.get(colors.$colors, 'white-ice');
    }
  }

  &.marker-top {
    background-color: map.get(colors.$colors, 'white');
    border-bottom: 1px solid map.get(colors.$colors, 'white');
    border-radius: 4px 4px 0 0;
    border-top: 4px solid map.get(colors.$colors, 'jewel');
    padding: 9px 16px;

    &:hover {
      background-color: map.get(colors.$colors, 'white-ice');
    }
  }
}
</style>
